import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Video, Clock, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { Helmet } from 'react-helmet-async';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import ShareProductModal from '../components/ShareProductModal';
import VideoPlayer from '../components/VideoPlayer';
import SocialShareWidget from '../components/SocialShareWidget';
import ArticleSchema from '../components/ArticleSchema';
import {
  applyBlogPostMetaDescriptionToDocument,
  resolveBlogPostMetaDescription,
} from '../utils/blogPostSeoMeta';
import { LIGHT_GUIDE_NEXT_STEPS } from '../config/lightCommercialRouting';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  slug: string;
  published_at: string;
  updated_at?: string;
  author: string;
  category: string;
  featured_image?: string;
  videos?: string[];
  meta_description?: string;
  og_title?: string;
  keywords?: string;
  focus_keyword?: string;
  reading_time?: number;
}

/** Natural next steps on ljud-related guides — routes traffic to money pages without rewriting CMS HTML. */
const LJUD_GUIDE_NEXT_STEPS: Record<
  string,
  { lead: string; links: { to: string; label: string }[] }
> = {
  'nyhet-2025-08-10-s-dimensionerar-du-rtt-ljudsystem-tekniken-bakom-valet': {
    lead: 'När ni vet ungefär vilken storlek ni behöver: vi hjälper er välja konkret lösning för fest i Malmö och Skåne.',
    links: [
      { to: '/hyra-hogtalare-malmo', label: 'Hyra högtalare i Malmö' },
      { to: '/kompletta-festpaket', label: 'Se kompletta festpaket' },
    ],
  },
};

const SCEN_GUIDE_NEXT_STEPS: Record<
  string,
  { lead: string; links: { to: string; label: string }[] }
> = {
  'nyhet-2026-01-22--nyhet-vi-erbjuder-nu-professionellt-scenbygge-i-samarbete-med-lunds-ljud': {
    lead:
      'Ska ni hyra scen eller få hjälp med komplett scenproduktion? Vi tar fram podier, tross och montering utifrån ert event.',
    links: [{ to: '/scen', label: 'Scenuthyrning i Skåne' }],
  },
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    // Signal to prerender that content is not ready yet
    if (typeof window !== 'undefined') {
      (window as any).prerenderReady = false;
    }

    // Set timeout to ensure prerender doesn't wait indefinitely
    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') {
        (window as any).prerenderReady = true;
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  // Sync title + description into the document *before* prerenderReady so Netlify
  // snapshots page-unique meta (Helmet alone can lose the race vs ready=true).
  // Same pattern as ProductDetailPage DOM meta write — no new marketing copy.
  useEffect(() => {
    if (loading) return;

    if (post) {
      const description = resolveBlogPostMetaDescription(post);
      document.title = post.og_title || `${post.title} - festutrustning.se`;
      applyBlogPostMetaDescriptionToDocument(description);
    }

    if (typeof window !== 'undefined') {
      (window as any).prerenderReady = true;
    }
  }, [loading, post]);

  const fetchBlogPost = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Write meta synchronously in the fetch path as well so prerender sees it
        // even if the effect below has not flushed yet.
        document.title = data.og_title || `${data.title} - festutrustning.se`;
        applyBlogPostMetaDescriptionToDocument(resolveBlogPostMetaDescription(data));
        setPost(data);

        // Fetch related posts from the same category
        const { data: related } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, published_at, category, featured_image, author')
          .eq('published', true)
          .eq('category', data.category)
          .neq('id', data.id)
          .order('published_at', { ascending: false })
          .limit(3);

        if (related) {
          setRelatedPosts(related);
        }
      } else {
        setError('Inlägget kunde inte hittas');
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError('Ett fel uppstod när inlägget skulle hämtas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: sv });
    } catch (e) {
      return dateString;
    }
  };

  const getCategoryLabel = (category: string) => {
    const categories: Record<string, { label: string, color: string }> = {
      'student': { label: 'Student', color: 'bg-blue-950/40 text-blue-200 border border-blue-900/50' },
      'företag': { label: 'Företag', color: 'bg-purple-950/40 text-purple-200 border border-purple-900/50' },
      'foretag': { label: 'Företag', color: 'bg-purple-950/40 text-purple-200 border border-purple-900/50' },
      'guider': { label: 'Guide', color: 'bg-green-950/40 text-green-200 border border-green-900/50' },
      'delivery': { label: 'Leverans', color: 'bg-yellow-950/40 text-yellow-200 border border-yellow-900/50' },
      'setup': { label: 'Riggning', color: 'bg-indigo-950/40 text-indigo-200 border border-indigo-900/50' },
      'event': { label: 'Event', color: 'bg-pink-950/40 text-pink-200 border border-pink-900/50' },
      'news': { label: 'Nyhet', color: 'bg-orange-950/40 text-orange-200 border border-orange-900/50' },
      'offer': { label: 'Erbjudande', color: 'bg-red-950/40 text-red-200 border border-red-900/50' }
    };

    return (
      categories[category?.toLowerCase()] || {
        label: category,
        color: 'bg-slate-800/60 text-slate-200 border border-slate-700',
      }
    );
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  if (loading) {
    return (
      <div className="pb-16 bg-transparent">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-800 rounded-none w-1/3 mb-8"></div>
            <div className="h-64 bg-slate-800 rounded-none mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-slate-800 rounded-none"></div>
              <div className="h-4 bg-slate-800 rounded-none w-5/6"></div>
              <div className="h-4 bg-slate-800 rounded-none w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pb-16 bg-transparent">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-4 text-white">Inlägget hittades inte</h1>
          <p className="text-slate-400 mb-6">Det inlägg du söker verkar inte finnas eller har tagits bort.</p>
          <Link
            to="/nyheter"
            className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors duration-300"
          >
            <ArrowLeft className="mr-2" size={18} />
            Tillbaka till alla nyheter
          </Link>
        </div>
      </div>
    );
  }

  const postUrl = `${window.location.origin}/nyheter/${post.slug}`;
  const categoryInfo = getCategoryLabel(post.category);
  const hasVideos = post.videos && post.videos.length > 0;
  const guideNextSteps =
    LIGHT_GUIDE_NEXT_STEPS[post.slug] ??
    LJUD_GUIDE_NEXT_STEPS[post.slug] ??
    SCEN_GUIDE_NEXT_STEPS[post.slug];

  return (
    <div className="pb-16 bg-transparent">
      <ArticleSchema
        title={post.title}
        description={resolveBlogPostMetaDescription(post)}
        author={post.author}
        publishedDate={post.published_at}
        modifiedDate={post.updated_at}
        imageUrl={post.featured_image}
        url={`https://festutrustning.se/nyheter/${post.slug}`}
        category={post.category}
        keywords={post.keywords}
        content={post.content}
        wordCount={post.reading_time ? post.reading_time * 200 : undefined}
      />
      <Helmet>
        <title>{post.og_title || `${post.title} - festutrustning.se`}</title>
        <meta name="description" content={resolveBlogPostMetaDescription(post)} />
        {post.keywords && <meta name="keywords" content={post.keywords} />}
        <link rel="canonical" href={`https://festutrustning.se/nyheter/${post.slug}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://festutrustning.se/nyheter/${post.slug}`} />
        <meta property="og:title" content={post.og_title || post.title} />
        <meta property="og:description" content={resolveBlogPostMetaDescription(post)} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        {post.featured_image && <meta property="og:image:width" content="1200" />}
        {post.featured_image && <meta property="og:image:height" content="630" />}
        <meta property="og:site_name" content="festutrustning.se" />
        <meta property="og:locale" content="sv_SE" />
        {post.created_at && <meta property="article:published_time" content={new Date(post.created_at).toISOString()} />}
        {post.updated_at && <meta property="article:modified_time" content={new Date(post.updated_at).toISOString()} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.og_title || post.title} />
        <meta name="twitter:description" content={resolveBlogPostMetaDescription(post)} />
        {post.featured_image && <meta name="twitter:image" content={post.featured_image} />}
      </Helmet>
      
      <div className="container mx-auto px-4">
        {/* Breadcrumbs */}
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-slate-400">
            <li>
              <Link to="/" className="hover:text-purple-300">
                Hem
              </Link>
            </li>
            <li>
              <ChevronRight size={16} className="text-slate-500" />
            </li>
            <li>
              <Link to="/nyheter" className="hover:text-purple-300">
                Nyheter
              </Link>
            </li>
            <li>
              <ChevronRight size={16} className="text-slate-500" />
            </li>
            <li className="text-white font-medium truncate max-w-xs">
              {post.title}
            </li>
          </ol>
        </nav>

        <div className="mb-6">
          <Link
            to="/nyheter"
            className="inline-flex items-center text-purple-300 hover:text-purple-200 font-medium"
          >
            <ArrowLeft size={18} className="mr-1" />
            Tillbaka till alla nyheter
          </Link>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="blog-surface rounded-none overflow-hidden">
            {post.featured_image && (
              <div className="relative h-64 md:h-96 bg-slate-900/70">
                <img 
                  src={post.featured_image} 
                  alt={post.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className={`${categoryInfo.color} px-3 py-1 rounded-full text-sm font-medium`}>
                    {categoryInfo.label}
                  </span>
                </div>
              </div>
            )}
            
            <div className="p-6 md:p-8 text-white">
              <div className="flex justify-between items-start mb-4">
                <h1 className="text-3xl font-bold">{post.title}</h1>
                <button 
                  onClick={handleShare}
                  className="text-purple-300 hover:text-purple-200 p-2 rounded-none hover:bg-purple-950/30 transition-colors"
                  title="Dela inlägg"
                >
                  <Share2 size={20} />
                </button>
              </div>
              
              <div className="flex flex-wrap items-center text-sm text-slate-400 mb-6 gap-2">
                <div className="flex items-center">
                  <Calendar size={14} className="mr-2" />
                  <span>{formatDate(post.published_at)}</span>
                </div>
                <span>•</span>
                <div className="flex items-center">
                  <User size={14} className="mr-1" />
                  <span>{post.author}</span>
                </div>
                {post.reading_time && (
                  <>
                    <span>•</span>
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1" />
                      <span>{post.reading_time} min läsning</span>
                    </div>
                  </>
                )}
              </div>
              
              <div className="mb-6">
                <SocialShareWidget 
                  url={postUrl}
                  title={post.title}
                  description={post.content.substring(0, 160)}
                  imageUrl={post.featured_image}
                  compact={true}
                />
              </div>
              
              <div
                dangerouslySetInnerHTML={{ __html: post.content }}
                className="blog-content max-w-none"
              />

              {guideNextSteps ? (
                <aside
                  className="mt-8 rounded-none border border-slate-700 bg-slate-900/50 p-5 md:p-6"
                  aria-label="Nästa steg"
                >
                  <p className="mb-4 text-sm leading-relaxed text-slate-300">
                    {guideNextSteps.lead}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {guideNextSteps.links.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-purple-300 underline decoration-purple-500/50 underline-offset-4 transition-colors hover:text-purple-200"
                      >
                        {item.label}
                        <ChevronRight size={16} aria-hidden />
                      </Link>
                    ))}
                  </div>
                </aside>
              ) : null}
              
              {/* Videos section */}
              {hasVideos && (
                <div className="mt-8">
                  <h2 className="text-xl font-bold mb-4">Videos</h2>
                  <div className="space-y-6">
                    {post.videos?.map((videoUrl, index) => (
                      <div key={index} className="rounded-none overflow-hidden">
                        <VideoPlayer url={videoUrl} className="w-full aspect-video" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-8 pt-6 border-t border-slate-600">
                <div className="flex justify-between items-center">
                  <Link 
                    to="/nyheter" 
                    className="inline-flex items-center text-purple-300 hover:text-purple-200 font-medium"
                  >
                    <ArrowLeft size={18} className="mr-1" />
                    Tillbaka till alla nyheter
                  </Link>
                  
                  <SocialShareWidget 
                    url={postUrl}
                    title={post.title}
                    description={post.content.substring(0, 160)}
                    imageUrl={post.featured_image}
                    compact={true}
                  />
                </div>
              </div>
              
              {/* Facebook comments section */}
              <div className="mt-8 pt-6 border-t border-slate-600">
                <h3 className="text-xl font-bold mb-4">Kommentarer</h3>
                <div className="fb-comments" data-href={postUrl} data-width="100%" data-numposts="5"></div>
              </div>
            </div>
          </div>

          {/* Related articles */}
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold mb-6 text-white">Relaterade artiklar</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => {
                  const relatedCategoryInfo = getCategoryLabel(relatedPost.category);
                  return (
                    <Link
                      key={relatedPost.id}
                      to={`/nyheter/${relatedPost.slug}`}
                      className="group blog-surface blog-surface--muted blog-surface--interactive block overflow-hidden rounded-none text-left"
                    >
                      <div className="relative h-40 bg-slate-900/75">
                        <img
                          src={relatedPost.featured_image || 'https://images.pexels.com/photos/2747446/pexels-photo-2747446.jpeg?auto=compress&cs=tinysrgb&w=800'}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span className={`${relatedCategoryInfo.color} px-2 py-1 rounded-full text-xs font-medium`}>
                            {relatedCategoryInfo.label}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold mb-2 line-clamp-2 text-white group-hover:text-purple-100 transition-colors">
                          {relatedPost.title}
                        </h3>
                        <div
                          className="blog-excerpt text-sm line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: relatedPost.excerpt }}
                        />
                        <div className="flex items-center text-xs text-slate-400 mt-3">
                          <Calendar size={12} className="mr-1" />
                          <span>{formatDate(relatedPost.published_at)}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {showShareModal && (
        <ShareProductModal
          productName={post.title}
          productUrl={postUrl}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default BlogPostPage;